// Fristende eines Assessments (Space.expiresAt) und seine Darstellung in Mails.
//
// Ein Tag ist ein Kalendertag in Europe/Berlin, die Frist endet um 23:59:59 Berlin.
// Der Browser kann in jeder Zeitzone laufen, deshalb rechnet alles hier über Intl mit
// fester timeZone statt mit Date#setHours.
//
// Kopie von aivy-backend-app, amplify/backend/function/sharedCode/assessmentDeadline.
// Änderungen immer an beiden Stellen, die Testfälle sind identisch. Nur so steht in
// Vorschau, Test-Mail und echter Mail dasselbe Datum. toPickerDate und
// pickerDateToExpiresAt am Ende gibt es nur im Dashboard.

const TIME_ZONE = 'Europe/Berlin'
const DEFAULT_EXPIRES_AFTER_DAYS = 14

// Wochentag und Monat liefert Intl, die Reihenfolge legen wir selbst fest. Je nach
// ICU-Version setzt en-GB sonst ein Komma nach dem Wochentag oder nicht, und Lambda
// und Browser haben nicht dieselbe ICU-Version.
const DEADLINE_FORMATS = {
  de: {
    locale: 'de-DE',
    pattern: ({ weekday, day, month, year }) =>
      `${weekday}, ${day}. ${month} ${year}`
  },
  en: {
    locale: 'en-GB',
    pattern: ({ weekday, day, month, year }) =>
      `${weekday}, ${day} ${month} ${year}`
  },
  nl: {
    locale: 'nl-NL',
    pattern: ({ weekday, day, month, year }) =>
      `${weekday} ${day} ${month} ${year}`
  },
  fr: {
    locale: 'fr-FR',
    pattern: ({ weekday, day, month, year }) =>
      `${weekday} ${day} ${month} ${year}`
  }
}
const DEADLINE_FALLBACK_LANGUAGE = 'en'

const berlinPartsFormatter = new Intl.DateTimeFormat('en-US', {
  timeZone: TIME_ZONE,
  hourCycle: 'h23',
  year: 'numeric',
  month: 'numeric',
  day: 'numeric',
  hour: 'numeric',
  minute: 'numeric',
  second: 'numeric'
})

const toParts = (formatter, date) => {
  const parts = {}
  formatter.formatToParts(date).forEach(({ type, value }) => {
    parts[type] = value
  })
  return parts
}

// Kalendertag und Uhrzeit von date in Berlin, month 1 bis 12
const berlinParts = (date) => {
  const { year, month, day, hour, minute, second } = toParts(
    berlinPartsFormatter,
    date
  )
  return {
    year: Number(year),
    month: Number(month),
    day: Number(day),
    hour: Number(hour),
    minute: Number(minute),
    second: Number(second)
  }
}

// Vorsprung der Berliner Uhr vor UTC zum Zeitpunkt date in ms (1 h Winter, 2 h Sommer)
const berlinOffset = (date) => {
  const { year, month, day, hour, minute, second } = berlinParts(date)
  const wallClock = Date.UTC(year, month - 1, day, hour, minute, second)
  return wallClock - Math.floor(date.getTime() / 1000) * 1000
}

// 23:59:59 Berlin am Kalendertag year-month-day. day darf über das Monatsende laufen,
// Date.UTC rechnet das in den Folgemonat um.
const berlinEndOfDay = ({ year, month, day }) => {
  const wallClock = Date.UTC(year, month - 1, day, 23, 59, 59)
  // Erster Schritt schätzt mit dem Offset um 23:59:59 UTC, der zweite korrigiert mit
  // dem Offset am Ergebnis. Die Zeitumstellung liegt nachts, nie um 23:59 Berlin.
  const estimate = wallClock - berlinOffset(new Date(wallClock))
  return new Date(wallClock - berlinOffset(new Date(estimate)))
}

const isValidDate = (value) => {
  if (value === null || value === undefined || value === '') return false
  return !Number.isNaN(new Date(value).getTime())
}

/**
 * Nur eine ganze Zahl über 0 zählt. null, ein fehlender oder ungültiger Wert
 * heißt "nicht gesetzt".
 *
 * @param {object} [app_settings]
 * @returns {number|null}
 */
export const readExpiresAfterDays = (app_settings) => {
  const days = Number(app_settings?.assessment_expires_after_days)
  return Number.isInteger(days) && days > 0 ? days : null
}

/**
 * Bearbeitungszeit in Tagen. Ein eigener Wert der Stelle geht vor, sonst gilt
 * der des Partners, ohne gültigen Wert 14. Einladung, Verlängerung, Test-Mail
 * und Dashboard rechnen alle hierüber, den Fallback gibt es nur an dieser Stelle.
 *
 * @param {object} [partnerSettings] app_settings des Partners
 * @param {object} [careerSettings] app_settings der Stelle, fehlt im Talentpool
 * @returns {number}
 */
export const getExpiresAfterDays = (partnerSettings, careerSettings) =>
  readExpiresAfterDays(careerSettings) ||
  readExpiresAfterDays(partnerSettings) ||
  DEFAULT_EXPIRES_AFTER_DAYS

/**
 * Fristende für eine Einladung oder Verlängerung ab from.
 *
 * @param {number} expiresAfterDays Bearbeitungszeit aus getExpiresAfterDays
 * @param {Date} [from] Zeitpunkt der Einladung bzw. Verlängerung
 * @returns {string} ISO-Zeitpunkt, Berliner Kalendertag von from plus N Tage, 23:59:59 Berlin
 */
export const computeExpiresAt = (expiresAfterDays, from = new Date()) => {
  const { year, month, day } = berlinParts(from)
  return berlinEndOfDay({
    year,
    month,
    day: day + expiresAfterDays
  }).toISOString()
}

/**
 * Verlängerungen kürzen nie. Gibt das spätere der beiden Fristenden zurück.
 *
 * @param {string} [current] bisheriges expiresAt, darf fehlen oder ungültig sein
 * @param {string} next neu berechnetes expiresAt
 * @returns {string} ISO-Zeitpunkt
 */
export const laterExpiresAt = (current, next) => {
  if (!isValidDate(current)) return next
  const currentDate = new Date(current)
  return currentDate.getTime() > new Date(next).getTime()
    ? currentDate.toISOString()
    : next
}

/**
 * Ersetzt {{assessment_deadline}} im Mailtext, z. B. "Donnerstag, 5. November 2026".
 *
 * @param {string} [expiresAt] Space.expiresAt
 * @param {string} language Sprache des Mailtexts, unbekannte fallen auf en
 * @returns {string} Datum mit Wochentag in Berliner Zeit, ohne gültiges expiresAt ''
 */
export const formatAssessmentDeadline = (expiresAt, language) => {
  if (!isValidDate(expiresAt)) return ''

  const { locale, pattern } =
    DEADLINE_FORMATS[language] || DEADLINE_FORMATS[DEADLINE_FALLBACK_LANGUAGE]
  const formatter = new Intl.DateTimeFormat(locale, {
    timeZone: TIME_ZONE,
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric'
  })

  return pattern(toParts(formatter, new Date(expiresAt)))
}

// Ab hier nur Dashboard

/**
 * react-datepicker und date-fns rechnen in der Zeitzone des Browsers. Damit sie
 * denselben Tag zeigen wie die Mail, bekommen sie ein lokales Datum mit dem
 * Berliner Kalendertag des Fristendes.
 *
 * @param {string} [expiresAt] Space.expiresAt
 * @returns {Date|null} lokales Datum 00:00 am Berliner Kalendertag
 */
export const toPickerDate = (expiresAt) => {
  if (!isValidDate(expiresAt)) return null
  const { year, month, day } = berlinParts(new Date(expiresAt))
  return new Date(year, month - 1, day)
}

/**
 * Gegenstück zu toPickerDate. Der im Picker gewählte Tag gilt als Berliner
 * Kalendertag, die Frist endet dort um 23:59:59.
 *
 * @param {Date} date lokales Datum aus dem Picker
 * @returns {string} ISO-Zeitpunkt
 */
export const pickerDateToExpiresAt = (date) =>
  berlinEndOfDay({
    year: date.getFullYear(),
    month: date.getMonth() + 1,
    day: date.getDate()
  }).toISOString()

/**
 * @param {string} [expiresAt] Space.expiresAt
 * @param {Date} [now]
 * @returns {boolean} true, wenn es ein Fristende gibt und es noch nicht erreicht ist
 */
export const isDeadlineRunning = (expiresAt, now = new Date()) =>
  isValidDate(expiresAt) && new Date(expiresAt).getTime() > now.getTime()

/**
 * Bearbeitungszeit für ein gemeinsames Datum mehrerer Talente, etwa beim
 * Verlängern. Haben alle Stellen dieselbe, gilt sie, sonst die des Partners.
 *
 * @param {object} [partnerSettings] app_settings des Partners
 * @param {Array<object|undefined>} careerSettingsList app_settings der Stelle je Talent
 * @returns {number}
 */
export const getCommonExpiresAfterDays = (
  partnerSettings,
  careerSettingsList
) => {
  const days = new Set(
    careerSettingsList.map((careerSettings) =>
      getExpiresAfterDays(partnerSettings, careerSettings)
    )
  )
  return days.size === 1 ? [...days][0] : getExpiresAfterDays(partnerSettings)
}
